import { api } from "@/redux/api";

import {
	BulkCreatePipedriveFieldMappings,
	AiTestRequest,
	AiTestResponse,
	AiTestResponseSchema,
	CreatePipedriveDealRequest,
	CreatePipedriveDealResponse,
	CreatePipedriveDealResponseSchema,
	CreatePipedriveFieldMapping,
	CreatePipedriveUserMapping,
	PipedriveCompanyDealCreationTriggerResponse,
	PipedriveCompanySyncTriggerResponse,
	PipedriveConversationSyncResponse,
	PipedriveDealConfig,
	PipedriveDealConfigResponse,
	PipedriveEntityType,
	PipedriveLeadConfig,
	PipedriveLeadConfigResponse,
	PipedriveFieldMapping,
	PipedriveFieldMappingsResponse,
	PipedriveFieldMappingsResponseSchema,
	PipedriveFieldsResponse,
	PipedriveListSyncTriggerResponse,
	PipedriveOAuthConnectResponse,
	PipedriveOAuthDisconnectResponse,
	PipedriveOAuthStatusResponse,
	PipedriveOwnersResponse,
	PipedrivePipelinesResponse,
	PipedriveResyncResponse,
	PipedriveSourceFieldsResponse,
	PipedriveSyncLogsResponse,
	PipedriveUserMapping,
	PipedriveUserMappingsResponse,
	UpdatePipedriveCompanyDealCreationTrigger,
	UpdatePipedriveCompanySyncTrigger,
	UpdatePipedriveConversationSync,
	UpdatePipedriveFieldMapping,
	UpdatePipedriveListSyncTrigger,
} from "../../pages/Company/schema/pipedrive";

export const pipedriveApi = api.injectEndpoints({
	endpoints: (builder) => ({
		// Field Retrieval
		getPipedrivePersonFields: builder.query<PipedriveFieldsResponse, void>({
			query: () => ({ url: "pipedrive/fields/persons", method: "GET" }),
			providesTags: ["Pipedrive"],
		}),
		getPipedriveOrganizationFields: builder.query<PipedriveFieldsResponse, void>({
			query: () => ({ url: "pipedrive/fields/organizations", method: "GET" }),
			providesTags: ["Pipedrive"],
		}),
		getPipedriveLeadFields: builder.query<PipedriveFieldsResponse, void>({
			query: () => ({ url: "pipedrive/fields/leads", method: "GET" }),
			providesTags: ["Pipedrive"],
		}),
		getPipedriveDealFields: builder.query<PipedriveFieldsResponse, void>({
			query: () => ({ url: "pipedrive/fields/deals", method: "GET" }),
			providesTags: ["Pipedrive"],
		}),
		getPipedriveSourceFields: builder.query<PipedriveSourceFieldsResponse, void>({
			query: () => ({ url: "pipedrive/source-fields", method: "GET" }),
			providesTags: ["Pipedrive"],
		}),

		// Field Mapping CRUD
		getPipedriveFieldMappings: builder.query<
			PipedriveFieldMappingsResponse,
			{ entityType?: PipedriveEntityType }
		>({
			query: (params) => ({
				url: "pipedrive/field-mappings",
				method: "GET",
				params,
			}),
			providesTags: ["Pipedrive"],
		}),
		createPipedriveFieldMapping: builder.mutation<
			PipedriveFieldMapping,
			CreatePipedriveFieldMapping
		>({
			query: (body) => ({ url: "pipedrive/field-mappings", method: "POST", body }),
			invalidatesTags: ["Pipedrive"],
		}),
		updatePipedriveFieldMapping: builder.mutation<
			PipedriveFieldMapping,
			{ id: number; data: UpdatePipedriveFieldMapping }
		>({
			query: ({ id, data }) => ({
				url: `pipedrive/field-mappings/${id}`,
				method: "PUT",
				body: data,
			}),
			invalidatesTags: ["Pipedrive"],
		}),
		deletePipedriveFieldMapping: builder.mutation<void, number>({
			query: (id) => ({ url: `pipedrive/field-mappings/${id}`, method: "DELETE" }),
			invalidatesTags: ["Pipedrive"],
		}),
		bulkCreatePipedriveFieldMappings: builder.mutation<
			PipedriveFieldMappingsResponse,
			BulkCreatePipedriveFieldMappings
		>({
			query: (body) => ({ url: "pipedrive/field-mappings/bulk", method: "POST", body }),
			invalidatesTags: ["Pipedrive"],
		}),
		/** "Add missing defaults" (PREC-4081): answers only the mappings it created. */
		addMissingPipedriveDefaultFieldMappings: builder.mutation<
			PipedriveFieldMappingsResponse,
			{ entityType?: PipedriveEntityType }
		>({
			query: (params) => ({
				url: "pipedrive/field-mappings/defaults",
				method: "POST",
				params,
				responseSchema: PipedriveFieldMappingsResponseSchema,
			}),
			invalidatesTags: ["Pipedrive"],
		}),
		testPipedriveAiFieldMapping: builder.mutation<AiTestResponse, AiTestRequest>({
			query: (body) => ({
				url: "pipedrive/field-mappings/ai/test",
				method: "POST",
				body,
				responseSchema: AiTestResponseSchema,
			}),
		}),

		// Sync Trigger - Company
		getPipedriveCompanySyncTrigger: builder.query<PipedriveCompanySyncTriggerResponse, void>({
			query: () => ({ url: "pipedrive/sync-trigger/company", method: "GET" }),
			providesTags: ["Pipedrive"],
		}),
		updatePipedriveCompanySyncTrigger: builder.mutation<
			PipedriveCompanySyncTriggerResponse,
			UpdatePipedriveCompanySyncTrigger
		>({
			query: (body) => ({ url: "pipedrive/sync-trigger/company", method: "PUT", body }),
			invalidatesTags: ["Pipedrive", "Me", "Company"],
		}),

		// Sync Trigger - List
		getPipedriveListSyncTrigger: builder.query<PipedriveListSyncTriggerResponse, number>({
			query: (listId) => ({ url: `pipedrive/sync-trigger/list/${listId}`, method: "GET" }),
			providesTags: ["Pipedrive"],
		}),
		updatePipedriveListSyncTrigger: builder.mutation<
			PipedriveListSyncTriggerResponse,
			{ listId: number; data: UpdatePipedriveListSyncTrigger }
		>({
			query: ({ listId, data }) => ({
				url: `pipedrive/sync-trigger/list/${listId}`,
				method: "PUT",
				body: data,
			}),
			invalidatesTags: ["Pipedrive"],
		}),

		// Deal Creation Trigger - Company
		getPipedriveCompanyDealCreationTrigger: builder.query<
			PipedriveCompanyDealCreationTriggerResponse,
			void
		>({
			query: () => ({ url: "pipedrive/deal-creation-trigger", method: "GET" }),
			providesTags: ["Pipedrive"],
		}),
		updatePipedriveCompanyDealCreationTrigger: builder.mutation<
			PipedriveCompanyDealCreationTriggerResponse,
			UpdatePipedriveCompanyDealCreationTrigger
		>({
			query: (body) => ({ url: "pipedrive/deal-creation-trigger", method: "PUT", body }),
			invalidatesTags: ["Pipedrive", "Me", "Company"],
		}),

		// Pipelines & Owners
		getPipedrivePipelines: builder.query<PipedrivePipelinesResponse, void>({
			query: () => ({ url: "pipedrive/pipelines", method: "GET" }),
			providesTags: ["Pipedrive"],
		}),
		getPipedriveOwners: builder.query<PipedriveOwnersResponse, void>({
			query: () => ({ url: "pipedrive/owners", method: "GET" }),
			providesTags: ["Pipedrive"],
		}),

		// Deal Configuration
		getPipedriveDealConfig: builder.query<PipedriveDealConfigResponse, void>({
			query: () => ({ url: "pipedrive/deal-config", method: "GET" }),
			providesTags: ["Pipedrive"],
		}),
		savePipedriveDealConfig: builder.mutation<PipedriveDealConfigResponse, PipedriveDealConfig>({
			query: (body) => ({ url: "pipedrive/deal-config", method: "PUT", body }),
			invalidatesTags: ["Pipedrive"],
		}),

		// Lead Configuration
		getPipedriveLeadConfig: builder.query<PipedriveLeadConfigResponse, void>({
			query: () => ({ url: "pipedrive/lead-config", method: "GET" }),
			providesTags: ["Pipedrive"],
		}),
		savePipedriveLeadConfig: builder.mutation<PipedriveLeadConfigResponse, PipedriveLeadConfig>({
			query: (body) => ({ url: "pipedrive/lead-config", method: "PUT", body }),
			invalidatesTags: ["Pipedrive"],
		}),

		// User Mappings
		getPipedriveUserMappings: builder.query<PipedriveUserMappingsResponse, void>({
			query: () => ({ url: "pipedrive/user-mappings", method: "GET" }),
			providesTags: ["Pipedrive"],
		}),
		createPipedriveUserMapping: builder.mutation<PipedriveUserMapping, CreatePipedriveUserMapping>({
			query: (body) => ({ url: "pipedrive/user-mappings", method: "POST", body }),
			invalidatesTags: ["Pipedrive"],
		}),
		deletePipedriveUserMapping: builder.mutation<void, number>({
			query: (id) => ({ url: `pipedrive/user-mappings/${id}`, method: "DELETE" }),
			invalidatesTags: ["Pipedrive"],
		}),

		// Create Deal/Lead
		createPipedriveDeal: builder.mutation<CreatePipedriveDealResponse, CreatePipedriveDealRequest>({
			query: (body) => ({
				url: "pipedrive/deals/create",
				method: "POST",
				body,
				responseSchema: CreatePipedriveDealResponseSchema,
			}),
			invalidatesTags: ["Pipedrive"],
		}),

		// Conversation Sync
		getPipedriveConversationSync: builder.query<PipedriveConversationSyncResponse, void>({
			query: () => ({ url: "pipedrive/conversation-sync", method: "GET" }),
			providesTags: ["Pipedrive"],
		}),
		updatePipedriveConversationSync: builder.mutation<
			PipedriveConversationSyncResponse,
			UpdatePipedriveConversationSync
		>({
			query: (body) => ({ url: "pipedrive/conversation-sync", method: "PUT", body }),
			invalidatesTags: ["Pipedrive"],
		}),

		// OAuth
		getPipedriveOAuthConnectUrl: builder.query<PipedriveOAuthConnectResponse, void>({
			query: () => ({ url: "pipedrive/oauth/connect", method: "GET" }),
		}),
		getPipedriveOAuthStatus: builder.query<PipedriveOAuthStatusResponse, void>({
			query: () => ({ url: "pipedrive/oauth/status", method: "GET" }),
			providesTags: ["Pipedrive"],
		}),
		disconnectPipedriveOAuth: builder.mutation<PipedriveOAuthDisconnectResponse, void>({
			query: () => ({ url: "pipedrive/oauth", method: "DELETE" }),
			invalidatesTags: ["Pipedrive", "Me", "Company"],
		}),

		// Manual Resync
		triggerPipedriveResync: builder.mutation<PipedriveResyncResponse, void>({
			query: () => ({ url: "pipedrive/sync/resync", method: "POST" }),
			invalidatesTags: ["Pipedrive"],
		}),

		// Sync Logs
		getPipedriveSyncLogs: builder.query<
			PipedriveSyncLogsResponse,
			{ entityType?: string; limit?: number; offset?: number } | void
		>({
			query: (params) => ({
				url: "pipedrive/sync/logs",
				method: "GET",
				params: params || undefined,
			}),
			providesTags: ["Pipedrive"],
		}),
	}),
});

export const {
	useGetPipedrivePersonFieldsQuery,
	useGetPipedriveOrganizationFieldsQuery,
	useGetPipedriveLeadFieldsQuery,
	useGetPipedriveDealFieldsQuery,
	useGetPipedriveSourceFieldsQuery,
	useGetPipedriveFieldMappingsQuery,
	useCreatePipedriveFieldMappingMutation,
	useUpdatePipedriveFieldMappingMutation,
	useDeletePipedriveFieldMappingMutation,
	useBulkCreatePipedriveFieldMappingsMutation,
	useTestPipedriveAiFieldMappingMutation,
	useGetPipedriveCompanySyncTriggerQuery,
	useUpdatePipedriveCompanySyncTriggerMutation,
	useGetPipedriveListSyncTriggerQuery,
	useUpdatePipedriveListSyncTriggerMutation,
	useGetPipedriveCompanyDealCreationTriggerQuery,
	useUpdatePipedriveCompanyDealCreationTriggerMutation,
	useGetPipedrivePipelinesQuery,
	useGetPipedriveOwnersQuery,
	useGetPipedriveDealConfigQuery,
	useSavePipedriveDealConfigMutation,
	useGetPipedriveLeadConfigQuery,
	useSavePipedriveLeadConfigMutation,
	useGetPipedriveUserMappingsQuery,
	useCreatePipedriveUserMappingMutation,
	useDeletePipedriveUserMappingMutation,
	useCreatePipedriveDealMutation,
	useGetPipedriveConversationSyncQuery,
	useUpdatePipedriveConversationSyncMutation,
	useLazyGetPipedriveOAuthConnectUrlQuery,
	useGetPipedriveOAuthStatusQuery,
	useDisconnectPipedriveOAuthMutation,
	useTriggerPipedriveResyncMutation,
	useGetPipedriveSyncLogsQuery,
	useAddMissingPipedriveDefaultFieldMappingsMutation,
} = pipedriveApi;

export const pipedriveApiReducer = pipedriveApi.reducer;
export const pipedriveApiMiddleware = pipedriveApi.middleware;
